import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import kubernetesPlugin from '@backstage/plugin-kubernetes/alpha';
import { NOT_PROJECT_ENTITY_FILTER, PROJECT_ENTITY_FILTER } from '../shared';
import { projectKubernetesFrTranslation } from './translation';

/**
 * The admin-only « Kubernetes » tab of an Odoo project page (2026-10-17),
 * in place of the upstream Kubernetes content (@backstage/plugin-kubernetes),
 * which an admin saw as the raw cluster objects and a member as « Warning:
 * Permission required ».
 *
 * TWO HALVES THAT MUST STAY MIRRORS. Our content takes the projects —
 * `PROJECT_ENTITY_FILTER` (components/shared/projectIdentity.ts), the filter
 * of every project card (`projectEnvironments/plugin.tsx`) — at the same path,
 * `/kubernetes`, so the header keeps its icon (`entityTabIcon`) and old links
 * still land. The upstream content keeps every other entity: its filter
 * (plugin-kubernetes 0.12.23 `alpha/entityContents.esm.js`: either Kubernetes
 * annotation) is restated with `NOT_PROJECT_ENTITY_FILTER`, its exact
 * negation. Two contents at one path are fine as long as no entity passes
 * both filters: the entity page drops the
 * contents whose filter refuses the entity before it builds its routes
 * (plugin-catalog alpha `filterEntityLayoutRoutes`).
 *
 * Overridden in code rather than with `config.filter` in the app-config
 * files: no configuration key to carry to every environment, and nothing an
 * instance can forget. A `config.filter` set there one day would still win
 * (it replaces the param, `resolveEntityFilterData`) — restate the project
 * exclusion in it then.
 *
 * Other entities keep the upstream tab and its reads through
 * `/api/kubernetes`. Only the content extension is overridden: the plugin's
 * API extensions stay, so `KubeSecretsCard` (`kubernetesApiRef`, its
 * `getClusters` and `proxy`) is not affected.
 */
const PROJECT_FILTER = PROJECT_ENTITY_FILTER;

const projectKubernetesContent = EntityContentBlueprint.make({
  name: 'project-kubernetes',
  params: {
    path: '/kubernetes',
    title: 'Kubernetes',
    filter: PROJECT_FILTER,
    loader: async () => {
      const { ProjectKubernetesTab } = await import('./ProjectKubernetesTab');
      return <ProjectKubernetesTab />;
    },
  },
});

/** On the `catalog` plugin, which owns the entity page it attaches to. */
export const projectKubernetesModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [projectKubernetesContent],
});

/** The upstream content, minus the Odoo projects. */
export const kubernetesContentFilterModule = createFrontendModule({
  pluginId: 'kubernetes',
  extensions: [
    kubernetesPlugin
      .getExtension('entity-content:kubernetes/kubernetes')
      .override({
        params: {
          filter: {
            $all: [
              {
                $any: [
                  {
                    'metadata.annotations.backstage.io/kubernetes-id': {
                      $exists: true,
                    },
                  },
                  {
                    'metadata.annotations.backstage.io/kubernetes-label-selector':
                      { $exists: true },
                  },
                ],
              },
              NOT_PROJECT_ENTITY_FILTER,
            ],
          },
        },
      }),
  ],
});

/**
 * The French messages, on the `app` plugin: a TranslationBlueprint under any
 * other pluginId is ignored without a word (see `projectEnvironments/plugin.tsx`).
 */
export const projectKubernetesTranslationModule = createFrontendModule({
  pluginId: 'app',
  extensions: [projectKubernetesFrTranslation],
});
